import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Container,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { FormattedMessage, useIntl } from 'react-intl';
import axiosConfig from 'src/Resources/axiosConfig';
import {
  TicketSummary,
  TicketStatus,
  TicketPriority,
  AdminUser,
  categoryKeys,
  subCategoryKeys,
  getCategoryDisplayLabel,
  getSubCategoryDisplayLabel
} from 'src/interfaces/TicketTypes';
import Footer from 'src/components/Footer';
import CreateTicketModal from './CreateTicketModal';
import StatusChip from './StatusChip';

const statusKeys: Record<number, { id: string; defaultMessage: string }> = {
  [TicketStatus.Open]:       { id: 'statusOpen',       defaultMessage: 'Open' },
  [TicketStatus.InProgress]: { id: 'statusInProgress', defaultMessage: 'In Progress' },
  [TicketStatus.Resolved]:   { id: 'statusResolved',   defaultMessage: 'Solved' }
};

const priorityKeys: Record<number, { id: string; defaultMessage: string }> = {
  [TicketPriority.Critical]: { id: 'priorityCritical', defaultMessage: 'Critical' },
  [TicketPriority.High]:     { id: 'priorityHigh',     defaultMessage: 'High' },
  [TicketPriority.Medium]:   { id: 'priorityMedium',   defaultMessage: 'Medium' },
  [TicketPriority.Low]:      { id: 'priorityLow',      defaultMessage: 'Low' }
};

function TicketList() {
  const navigate = useNavigate();
  const intl = useIntl();
  const [tickets, setTickets] = useState<TicketSummary[]>([]);
  const [adminUsers, setAdminUsers] = useState<AdminUser[]>([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<number[]>([]);
  const [partnerFilter, setPartnerFilter] = useState<string>('');
  const [assigneeFilter, setAssigneeFilter] = useState<string>('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [createOpen, setCreateOpen] = useState(false);
  const [error, setError] = useState('');

  const fetchTickets = () => {
    axiosConfig
      .get('/GetTicketSummaries')
      .then((res) => setTickets(res.data))
      .catch(() => setError('Failed to load tickets'));
  };

  useEffect(() => {
    fetchTickets();
    axiosConfig
      .get('/GetAdminUsers')
      .then((res) => setAdminUsers(res.data))
      .catch(() => {});
  }, []);

  // Group partner names that differ only by case / whitespace so the same
  // partner does not appear as multiple dropdown entries (e.g. "Breu AG" vs
  // "Breu Ag"). Normalize for matching, but show one canonical label per group.
  const normalizePartner = (p: string) =>
    p.trim().replace(/\s+/g, ' ').toLowerCase();

  const partnerOptions = Array.from(
    tickets
      .map((t) => t.distributionPartner)
      .filter((p) => p && p.trim() !== '')
      .reduce((map, p) => {
        const key = normalizePartner(p);
        const display = p.trim().replace(/\s+/g, ' ');
        const current = map.get(key);
        // One canonical label per group; pick the alphabetically first spelling.
        if (!current || display.localeCompare(current) < 0) map.set(key, display);
        return map;
      }, new Map<string, string>())
      .values()
  ).sort((a, b) => a.localeCompare(b));

  const assigneeOptions = adminUsers
    .filter((u) => {
      const name = (u.name ?? '').toLowerCase();
      return (
        !name.includes('inesco energy master admin') &&
        !name.includes('paal myhre')
      );
    })
    .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));

  const filtered = tickets
    .filter((t) => {
      const matchesSearch =
        search === '' ||
        t.subject.toLowerCase().includes(search.toLowerCase()) ||
        t.installationName.toLowerCase().includes(search.toLowerCase());
      const matchesStatus = statusFilter.length === 0 || statusFilter.includes(t.status);
      const matchesPartner =
        partnerFilter === '' ||
        normalizePartner(t.distributionPartner ?? '') === normalizePartner(partnerFilter);
      const matchesAssignee =
        assigneeFilter === '' ||
        (assigneeFilter === '__unassigned__'
          ? !t.assigneeId
          : t.assigneeId === Number(assigneeFilter));
      const matchesCategory =
        categoryFilter === '' || t.category === Number(categoryFilter);
      return matchesSearch && matchesStatus && matchesPartner && matchesAssignee && matchesCategory;
    })
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  return (
    <div style={{ userSelect: 'none' }}>
      <Container maxWidth="xl" sx={{ marginTop: '20px' }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h3">
            <FormattedMessage id="tickets" defaultMessage="Tickets" />
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateOpen(true)}
          >
            <FormattedMessage
              id="createTicket"
              defaultMessage="Create Ticket"
            />
          </Button>
        </Box>

        <Box display="flex" gap={2} mb={2}>
          <TextField
            size="small"
            label={<FormattedMessage id="search" defaultMessage="Search" />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 250 }}
          />
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>
              <FormattedMessage id="status" defaultMessage="Status" />
            </InputLabel>
            <Select
              multiple
              value={statusFilter}
              label={intl.formatMessage({ id: 'status', defaultMessage: 'Status' })}
              onChange={(e) => {
                const value = e.target.value;
                setStatusFilter(
                  typeof value === 'string'
                    ? value.split(',').map(Number)
                    : (value as number[])
                );
              }}
              renderValue={(selected) =>
                (selected as number[])
                  .map((s) => intl.formatMessage(statusKeys[s]))
                  .join(', ')
              }
            >
              {Object.entries(statusKeys).map(([val, msg]) => {
                const num = Number(val);
                return (
                  <MenuItem key={val} value={num}>
                    <Checkbox checked={statusFilter.indexOf(num) > -1} />
                    <ListItemText primary={intl.formatMessage(msg)} />
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>
              <FormattedMessage
                id="distributionPartner"
                defaultMessage="Distribution Partner"
              />
            </InputLabel>
            <Select
              value={partnerFilter}
              label={intl.formatMessage({
                id: 'distributionPartner',
                defaultMessage: 'Distribution Partner'
              })}
              onChange={(e) => setPartnerFilter(e.target.value as string)}
            >
              <MenuItem value="">
                <FormattedMessage id="allPartners" defaultMessage="All Partners" />
              </MenuItem>
              {partnerOptions.map((p) => (
                <MenuItem key={p} value={p}>
                  {p}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>
              <FormattedMessage id="assignee" defaultMessage="Assignee" />
            </InputLabel>
            <Select
              value={assigneeFilter}
              label={intl.formatMessage({
                id: 'assignee',
                defaultMessage: 'Assignee'
              })}
              onChange={(e) => setAssigneeFilter(e.target.value as string)}
            >
              <MenuItem value="">
                <FormattedMessage id="allAssignees" defaultMessage="All Assignees" />
              </MenuItem>
              <MenuItem value="__unassigned__">
                <FormattedMessage id="unassigned" defaultMessage="Unassigned" />
              </MenuItem>
              {assigneeOptions.map((u) => (
                <MenuItem key={u.id} value={String(u.id)}>
                  {u.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>
              <FormattedMessage id="category" defaultMessage="Category" />
            </InputLabel>
            <Select
              value={categoryFilter}
              label={intl.formatMessage({
                id: 'category',
                defaultMessage: 'Category'
              })}
              onChange={(e) => setCategoryFilter(e.target.value as string)}
            >
              <MenuItem value="">
                <FormattedMessage id="allCategories" defaultMessage="All Categories" />
              </MenuItem>
              {Object.entries(categoryKeys).map(([val, msg]) => (
                <MenuItem key={val} value={val}>
                  {intl.formatMessage(msg)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {filtered.length === 0 && !error ? (
          <Alert severity="info">
            <FormattedMessage
              id="noTickets"
              defaultMessage="No tickets found."
            />
          </Alert>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>
                    <FormattedMessage
                      id="subject"
                      defaultMessage="Subject"
                    />
                  </TableCell>
                  <TableCell>
                    <FormattedMessage
                      id="status"
                      defaultMessage="Status"
                    />
                  </TableCell>
                  <TableCell>
                    <FormattedMessage
                      id="installation"
                      defaultMessage="Installation"
                    />
                  </TableCell>
                  <TableCell>
                    <FormattedMessage
                      id="distributionPartner"
                      defaultMessage="Distribution Partner"
                    />
                  </TableCell>
                  <TableCell>
                    <FormattedMessage
                      id="priority"
                      defaultMessage="Priority"
                    />
                  </TableCell>
                  <TableCell>
                    <FormattedMessage
                      id="category"
                      defaultMessage="Category"
                    />
                  </TableCell>
                  <TableCell>
                    <FormattedMessage
                      id="createdAt"
                      defaultMessage="Created"
                    />
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((ticket) => {
                  const catLabel = getCategoryDisplayLabel(ticket.category, ticket.customCategory);
                  const catKey = categoryKeys[ticket.category];
                  const catDisplay = catKey
                    ? intl.formatMessage(catKey)
                    : catLabel;
                  const subLabel = getSubCategoryDisplayLabel(ticket.subCategory, ticket.customSubCategory);
                  const subKey = subCategoryKeys[ticket.subCategory];
                  const subDisplay = subKey
                    ? intl.formatMessage(subKey)
                    : subLabel;

                  return (
                    <TableRow key={ticket.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/tickets/${ticket.id}`)}>
                      <TableCell>{ticket.id}</TableCell>
                      <TableCell>{ticket.subject}</TableCell>
                      <TableCell>
                        <StatusChip status={ticket.status} />
                      </TableCell>
                      <TableCell>{ticket.installationName}</TableCell>
                      <TableCell>{ticket.distributionPartner}</TableCell>
                      <TableCell>{intl.formatMessage(priorityKeys[ticket.priority] ?? { id: 'unknown', defaultMessage: '-' })}</TableCell>
                      <TableCell>
                        {ticket.customCategory
                          ? ticket.customCategory
                          : catDisplay}
                        {subLabel !== 'Other' && subLabel !== 'Unknown' && subLabel !== 'General'
                          ? ` — ${ticket.customSubCategory || subDisplay}`
                          : ticket.customSubCategory
                            ? ` — ${ticket.customSubCategory}`
                            : ''}
                      </TableCell>
                      <TableCell>
                        {new Date(ticket.createdAt).toLocaleDateString()}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <CreateTicketModal
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          onCreated={fetchTickets}
        />
      </Container>
      <Footer />
    </div>
  );
}

export default TicketList;
